import { useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Container,
  Paper,
  Box,
  Typography,
  Button,
  CircularProgress,
  Alert,
  ToggleButtonGroup,
  ToggleButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
} from '@mui/material';
import { useQuery, useMutation } from '@tanstack/react-query';
import { 
  Refresh as RefreshIcon, 
  CalendarMonth,
  InfoOutlined,
  WarningAmberOutlined,
} from '@mui/icons-material';
import MomentumBadge from '../components/momentum/MomentumBadge';
import MomentumDeltasTable from '../components/momentum/MomentumDeltasTable';
import { momentumService } from '../services/momentumService';

export default function MomentumPage() {
  const { pageId } = useParams<{ pageId: string }>();
  const [engine, setEngine] = useState<'google' | 'bing'>('google');
  const [window, setWindow] = useState<28 | 90>(28);

  if (!pageId) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="error">Page ID not found</Alert>
      </Container>
    );
  }

  const numericPageId = parseInt(pageId, 10);

  // Fetch momentum data
  const momentumQuery = useQuery({
    queryKey: ['momentum', numericPageId, engine, window],
    queryFn: () => momentumService.getMomentum(numericPageId, engine, window),
    refetchInterval: 60000, // Refresh every 60s
  });

  // Fetch momentum history
  const historyQuery = useQuery({
    queryKey: ['momentumHistory', numericPageId, engine],
    queryFn: () => momentumService.getMomentumHistory(numericPageId, engine, 10),
    enabled: !!numericPageId,
  });

  // Recompute mutation
  const recomputeMutation = useMutation({
    mutationFn: () => momentumService.recomputeMomentum(numericPageId, engine, window),
    onSuccess: () => {
      momentumQuery.refetch();
      historyQuery.refetch();
    },
  });

  const momentumData = momentumQuery.data;

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" gutterBottom>
          Performance Momentum
        </Typography>
        <Typography variant="body2" color="textSecondary">
          Page #{pageId} - Trend Analysis
        </Typography>
      </Box>

      {/* Controls */}
      <Paper elevation={2} sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          {/* Engine Selector */}
          <ToggleButtonGroup
            value={engine}
            exclusive
            onChange={(_, value) => value && setEngine(value)}
            size="small"
          >
            <ToggleButton value="google">
              Google
            </ToggleButton>
            <ToggleButton value="bing">
              Bing
            </ToggleButton>
          </ToggleButtonGroup>

          {/* Window Selector */}
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Time Window</InputLabel>
            <Select
              value={window}
              label="Time Window"
              onChange={(e) => setWindow(e.target.value as 28 | 90)}
            >
              <MenuItem value={28}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CalendarMonth sx={{ fontSize: 18 }} />
                  28 days
                </Box>
              </MenuItem>
              <MenuItem value={90}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CalendarMonth sx={{ fontSize: 18 }} />
                  90 days
                </Box>
              </MenuItem>
            </Select>
          </FormControl>

          <Box sx={{ flex: 1 }} />

          {/* Recompute Button */}
          <Button
            variant="outlined"
            size="small"
            startIcon={<RefreshIcon />}
            onClick={() => recomputeMutation.mutate()}
            disabled={recomputeMutation.isPending || momentumQuery.isLoading}
          >
            Recompute
          </Button>
        </Box>
      </Paper>

      {momentumQuery.isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : momentumQuery.isError ? (
        <Alert severity="error">Failed to load momentum data</Alert>
      ) : momentumData ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {/* Low Volume Warning */}
          {momentumData.isLowVolume && (
            <Alert severity="warning" icon={<WarningAmberOutlined />}>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                Low Traffic Volume
              </Typography>
              <Typography variant="caption">
                Momentum analysis may be unstable due to low impression/click volume. 
                Consider increasing the time window to 90 days for more reliable trends.
              </Typography>
            </Alert>
          )}

          {/* Momentum Status Card */}
          <Paper elevation={2} sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
              <Box>
                <Typography variant="h6" gutterBottom>
                  Current Momentum
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  Computed {new Date(momentumData.computedAt).toLocaleString()}
                </Typography>
              </Box>
              <MomentumBadge
                state={momentumData.state}
                confidence={momentumData.confidence}
                size="medium"
                showConfidence
              />
            </Box>

            <Divider sx={{ my: 2 }} />

            {/* Rationale */}
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
              <InfoOutlined color="primary" sx={{ fontSize: 20, mt: 0.5 }} />
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Why This Assessment?
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  {momentumData.rationale}
                </Typography>
              </Box>
            </Box>
          </Paper>

          {/* Deltas Table */}
          <MomentumDeltasTable deltas={momentumData.deltas} />

          {/* Action Recommendations */}
          {momentumData.state === 'DECLINING' && (
            <Alert severity="warning">
              <Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }}>
                Recommended Actions
              </Typography>
              <Typography variant="caption" component="div">
                • Run SEO validation to identify technical issues<br />
                • Review recent content changes<br />
                • Check for SERP feature changes<br />
                • Consider creating an action item or experiment
              </Typography>
            </Alert>
          )}

          {momentumData.state === 'IMPROVING' && (
            <Alert severity="success">
              <Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }}>
                Positive Momentum Detected
              </Typography>
              <Typography variant="caption">
                Performance is improving. Consider documenting recent changes that may have contributed 
                to this improvement for future reference.
              </Typography>
            </Alert>
          )}

          {/* History Preview */}
          {historyQuery.data && historyQuery.data.length > 0 && (
            <Paper elevation={1} sx={{ p: 2 }}>
              <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 600, mb: 2 }}>
                Recent History
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {historyQuery.data.slice(0, 7).reverse().map((item, index) => (
                  <MomentumBadge
                    key={index}
                    state={item.state}
                    size="small"
                  />
                ))}
              </Box>
              <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
                Last 7 assessments (oldest to newest)
              </Typography>
            </Paper>
          )}
        </Box>
      ) : null}
    </Container>
  );
}
